import React, { useState, useEffect, useCallback } from 'react';
import './report-card-print.css';

// Material UI
import Container from '@material-ui/core/Container';
import IconButton from '@material-ui/core/IconButton';
import PrintIcon from '@material-ui/icons/Print';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';

// Components
import FullPageLayout from '../_layout/FullPageLayout/full-page.layout';
import { ReportCardGraphQL } from '../../Score/ReportCard/report-card.graphql';
import { useCompanyHasReportCards } from '../../../lib/stores/company.store';
import { treeFromPath } from '../../../lib/services/auth.service';
import { SessionType } from '../../../lib/types/session.types';
import { Redirect, useLocation } from 'react-router';
import { Box } from '@material-ui/core';
import { GoogleAdsComponent } from '../../_core/GoogleAds/google-ads.component';
import { ReportCardChatWidget } from '../../_core/Chatbot/report-card-chat-widget.component';
import { useChatbotAccess } from '../../../lib/services/chatbot.service';
import { store, initializeStorage } from '../../../lib/utils/storage';
import { PARENT_HOME_ROUTE } from '../../../../_configuration';

const useStyles = makeStyles((theme) => ({
  floatingPrintButton: {
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: 1000,
    background: '#fff',
    boxShadow: theme.shadows[3],
    '@media print': {
      display: 'none',
    },
  },
  printButtonPreparing: {
    background: '#f0f0f0',
    color: '#666',
    cursor: 'wait',
  },
  containerRelative: {
    position: 'relative',
    minWidth: '770px',
    // Make responsive for smaller screens
    [theme.breakpoints.down('md')]: {
      minWidth: 'unset',
      width: '100%',
    },
    // Remove horizontal padding on small screens
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
}));

interface IReportCardPage {
  match: any;
  location: any;
}

export const ReportCardPage: React.FC<IReportCardPage> = ({ match, location }) => {
  const { code, playerAssessmentId } = match.params;
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');
  const hasReportCards = useCompanyHasReportCards();
  const { hasChatbot, loading: chatbotLoading } = useChatbotAccess({
    playerAssessmentId,
    token: token || undefined,
  });
  const { pathname } = useLocation();
  const classes = useStyles();
  const [isPreparing, setIsPreparing] = useState(false);
  const [authToken, setAuthToken] = useState<string | undefined>(undefined);

  // Determined before every hook below: both the storage-key selection and the
  // page-level gate fork on it (hooks must run unconditionally).
  const isParentTree = treeFromPath(pathname) === SessionType.PARENT;

  // Read the SPA's stored bearer token, picking the storage key for whichever
  // tree the page is mounted in — parent-tree reads use the parent JWT.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await initializeStorage();
        const key = isParentTree ? 'mea_parent_token' : 'mea_token';
        const t = await store.get(key);
        if (!cancelled && typeof t === 'string') setAuthToken(t);
      } catch (err) {
        console.error('ReportCardPage: failed to read auth token from storage', err);
      }
    })();
    return () => { cancelled = true; };
  }, [isParentTree]);

  // Print handler with extra delay for accordion expansion
  const handlePrint = useCallback(async () => {
    setIsPreparing(true);

    try {
      // Single dispatch to trigger accordion expansions
      window.dispatchEvent(new CustomEvent('prepare-for-print'));

      // Wait longer to ensure all accordions are fully expanded
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Quick check for accordion expansion
      const accordions = document.querySelectorAll('.MuiAccordion-root');
      if (accordions.length > 0) {
        // Brief check if accordions need more time to expand
        const expandedAccordions = document.querySelectorAll('.MuiAccordion-expanded, .MuiCollapse-entered');
        if (expandedAccordions.length < accordions.length) {
          // One additional wait if needed
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

    } catch (error) {
      console.warn('Error preparing for print:', error);
    } finally {
      setIsPreparing(false);
      window.print();
    }
  }, []);


  // `companyHasReportCards` is set during staff bootstrap; the parent app
  // never initializes it, so the parent tree bypasses this staff-side gate.
  if (!hasReportCards && !token && !isParentTree) return <Redirect to="/" />;
  return (
    <FullPageLayout
      parentHeader={{ title: 'Report card', backTo: PARENT_HOME_ROUTE, backLabel: 'My players' }}
    >
      <Container className={classes.containerRelative} maxWidth="xl">
        {/* Print Button */}
        <IconButton
          className={`${classes.floatingPrintButton} ${isPreparing ? classes.printButtonPreparing : ''}`}
          aria-label="Print or Download Report Card"
          onClick={handlePrint}
          disabled={isPreparing}
        >
          {isPreparing ? <CircularProgress size={24} /> : <PrintIcon />}
        </IconButton>
        <Box display="flex" flexDirection="row" alignItems="start" justifyContent="space-between" style={{ gap: "5rem" }}>
          <GoogleAdsComponent adSlot="9158073510" allowedDevices={["desktop"]} />
          <ReportCardGraphQL
            code={code}
            playerAssessmentId={playerAssessmentId}
            token={token || undefined}
          />
          <GoogleAdsComponent adSlot="8585064731" allowedDevices={["desktop"]} />
        </Box>
      </Container>
      {!chatbotLoading && hasChatbot && (
        <ReportCardChatWidget
          playerAssessmentId={playerAssessmentId}
          token={token || undefined}
          authToken={authToken}
        />
      )}
    </FullPageLayout>
  );
};
